import { useEffect, useState } from "react";
import {
  formatPaymentActivity,
  useFetchPaymentActivityById,
} from "byzantine/src/dbbl/businessLogic/entities/paymentActivity";
import {
  useAllRecipients,
  useFetchSwiftAdditionalDetails,
  useFetchSwiftAdditionalFieldsByCountry,
} from "byzantine/src/dbbl/businessLogic/entities/recipients";
import { useAllAccounts } from "byzantine/src/dbbl/businessLogic/entities";
import { useFetchBulkPaymentById } from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import { useFetchBatch } from "byzantine/src/dbbl/businessLogic/entities/wireImport/requests";
import type { PaymentMetadataProps } from "./PaymentActivityDrawer";

type UsePaymentDrawerDataArgs = {
  payment: API.FormattedPaymentActivity | null;
  paymentUuid:
    | API.ScheduledACHPaymentId
    | API.ACHPaymentId
    | API.SwiftTransactionId;
};

export type PaymentDrawerData = {
  paymentActivity: API.FormattedPaymentActivity | null;
  paymentMetadata: PaymentMetadataProps | null;
  institutionDetails: { [key: string]: string } | null;
  renameFields: { [key: string]: string };
  correspondentBankName: string | null;
  bulkTitle: string;
  isBulkImported: boolean;
  wireImportBatchTitle: string;
  loading: boolean;
};

/**
 * Consolidates all data fetching + derived server state for the payment
 * activity drawer: the payment activity itself, its metadata, associated
 * bulk/wire-import titles, and SWIFT institution / correspondent bank details.
 */
const usePaymentDrawerData = ({
  payment,
  paymentUuid,
}: UsePaymentDrawerDataArgs): PaymentDrawerData => {
  const [paymentActivity, setPaymentActivity] =
    useState<API.FormattedPaymentActivity | null>(payment ?? null);
  // metadata is stored with the request identity that produced it so a slow
  // response can never be attributed to a different payment
  const [metadataResult, setMetadataResult] = useState<{
    uuid: string;
    model: string;
    data: PaymentMetadataProps | null;
  } | null>(null);
  const [institutionDetails, setInstitutionDetails] = useState<{
    [key: string]: string;
  } | null>(null);
  const [renameFields, setRenameFields] = useState<{
    [key: string]: string;
  }>({});
  const [correspondentBankName, setCorrespondentBankName] = useState<
    string | null
  >(null);
  const [bulkTitle, setBulkTitle] = useState("");
  const [isBulkImported, setIsBulkImported] = useState(false);
  const [wireImportBatchTitle, setWireImportBatchTitle] = useState("");

  const { send: fetchPaymentActivityById } = useFetchPaymentActivityById();
  // its own request instance, so `loading` tracks only the details fetch
  const { send: fetchPaymentDetails, loading } = useFetchPaymentActivityById();
  const { send: fetchBulkPaymentById } = useFetchBulkPaymentById();
  const { send: fetchWireImportBatchById } = useFetchBatch();
  const { fetchSwiftAdditionalDetails } = useFetchSwiftAdditionalDetails();
  const { fetchSwiftAdditionalFieldsByCountry } =
    useFetchSwiftAdditionalFieldsByCountry();
  const recipients = useAllRecipients();
  const accounts = useAllAccounts();

  // if payment activity doesn't exist, fetch it
  useEffect(() => {
    if (!payment && paymentUuid) {
      fetchPaymentActivityById(paymentUuid)
        .then((response) => {
          const formattedData = formatPaymentActivity(
            response as API.PaymentActivity,
            recipients,
            accounts
          );
          setPaymentActivity(formattedData);
        })
        .catch((e) => console.error("Error fetching payment activity:", e));
    }
  }, [payment, paymentUuid, recipients, accounts]);

  useEffect(() => {
    if (payment) {
      setPaymentActivity(payment);
    }
  }, [payment]);

  useEffect(() => {
    // ignore in-flight responses once the selected payment changes
    let isCurrent = true;

    const applyMetadata = (
      uuid: string,
      model: string,
      data: PaymentMetadataProps | null
    ) => {
      if (!isCurrent) return;
      setMetadataResult({ uuid, model, data });
    };

    const fetchSinglePaymentMetadata = async () => {
      if (!paymentActivity?.uuid || !paymentActivity?.model) return;
      // read from Payment activity rather than the rail endpoint, which
      // rejects roles that can only view the account. A reversal's details
      // are its original payment's.
      const { uuid, model } = paymentActivity;
      try {
        const response = await fetchPaymentDetails(uuid);
        applyMetadata(
          uuid,
          model,
          (response?.payment_details as PaymentMetadataProps | null) ?? null
        );
      } catch (error) {
        applyMetadata(uuid, model, null);
      }
    };

    const fetchBulkPaymentDetails = async () => {
      if (paymentActivity?.bulkPaymentId) {
        try {
          const data = await fetchBulkPaymentById(
            paymentActivity.bulkPaymentId
          );
          if (isCurrent) setBulkTitle(data?.title ?? "");
          if (isCurrent) setIsBulkImported(!!data?.organization_file);
        } catch (error) {
          if (isCurrent) setBulkTitle("");
        }
      }
    };

    const fetchWireImportBatchDetails = async () => {
      if (paymentActivity?.wireImportBatchId) {
        try {
          const data = await fetchWireImportBatchById(
            paymentActivity.wireImportBatchId
          );
          if (isCurrent) setWireImportBatchTitle(data?.description ?? "");
        } catch (error) {
          if (isCurrent) setWireImportBatchTitle("");
        }
      }
    };

    setBulkTitle("");
    setIsBulkImported(false);
    setWireImportBatchTitle("");
    fetchSinglePaymentMetadata();
    fetchBulkPaymentDetails();
    fetchWireImportBatchDetails();

    return () => {
      isCurrent = false;
    };
  }, [paymentActivity]);

  useEffect(() => {
    // reset before (re)fetching so a non-international payment, or one without
    // SWIFT details, doesn't show stale labels from a previously viewed wire
    setInstitutionDetails(null);
    setRenameFields({});

    const swiftBic =
      paymentActivity?.destinationDetails?.bic ||
      paymentActivity?.recipient?.swift_destination?.bic;

    if (!paymentActivity?.isInternationalWirePayment || !swiftBic) {
      return;
    }

    const fetchAndSetAdditionalDetails = async (bic: string) => {
      const res = await fetchSwiftAdditionalDetails(bic);
      if (res?.additional_fields) {
        setInstitutionDetails({
          institutionName: res.institution_name,
        });
        setRenameFields(res.rename_fields);
      }
    };

    fetchAndSetAdditionalDetails(swiftBic).catch(() => {
      setInstitutionDetails(null);
      setRenameFields({});
    });
  }, [
    paymentActivity,
    paymentActivity?.isInternationalWirePayment,
    fetchSwiftAdditionalDetails,
  ]);

  // Fetch correspondent bank name and country-based rename fields
  useEffect(() => {
    // reset before (re)fetching so navigating away from a wire with a
    // correspondent bank doesn't leave the previous name visible
    setCorrespondentBankName(null);

    const correspondentBic =
      paymentActivity?.recipient?.swift_destination?.correspondent_bank_bic;

    if (!paymentActivity?.isInternationalWirePayment || !correspondentBic) {
      return;
    }

    const fetchCorrespondentBankDetails = async (bic: string) => {
      const res = await fetchSwiftAdditionalDetails(bic);
      if (res?.institution_name) {
        setCorrespondentBankName(res.institution_name);
      }
      const countryCode =
        paymentActivity?.recipient?.swift_destination?.branch_address
          ?.country_code;
      if (countryCode) {
        const countryDetails = await fetchSwiftAdditionalFieldsByCountry(
          countryCode
        );
        if (countryDetails?.rename_fields) {
          setRenameFields(countryDetails.rename_fields);
        }
      }
    };

    fetchCorrespondentBankDetails(correspondentBic).catch(() => {
      setCorrespondentBankName(null);
    });
  }, [
    paymentActivity,
    paymentActivity?.isInternationalWirePayment,
    fetchSwiftAdditionalDetails,
    fetchSwiftAdditionalFieldsByCountry,
  ]);

  // only surface metadata that belongs to the currently selected payment, so a
  // stale response is never read while the next payment's fetch is in flight
  const paymentMetadata =
    metadataResult &&
    paymentActivity &&
    metadataResult.uuid === paymentActivity.uuid &&
    metadataResult.model === paymentActivity.model
      ? metadataResult.data
      : null;

  return {
    paymentActivity,
    paymentMetadata,
    institutionDetails,
    renameFields,
    correspondentBankName,
    bulkTitle,
    isBulkImported,
    wireImportBatchTitle,
    loading,
  };
};

export default usePaymentDrawerData;
